import { isAxiosError } from "axios";

/**
 * Checks if the given error comes from a request rejected because the user is
 * not allowed to perform it, either by session (401) or by permission (403).
 * @param error - The error caught from an axios request or a react-query hook.
 * @returns True if the error is a 401 or 403 response, false otherwise.
 */
export const isAccessDeniedError = (error: unknown): boolean =>
  isAxiosError(error) &&
  (error.response?.status === 401 || error.response?.status === 403);

/**
 * Checks if the given error is a network failure or client-side connection issue
 * where no response was received (e.g. user lost internet connection, tab closed, timeout, request canceled).
 * @param error - The error to check.
 * @returns True if the error is a network/connectivity error, false otherwise.
 */
export const isNetworkError = (error: unknown): boolean => {
  if (isAxiosError(error)) {
    return (
      !error.response ||
      error.code === "ERR_NETWORK" ||
      error.code === "ECONNABORTED" ||
      error.code === "ERR_CANCELED" ||
      error.message === "Network Error"
    );
  }
  if (error instanceof Error) {
    return (
      error.message === "Network Error" ||
      error.message === "Failed to fetch" ||
      error.name === "CanceledError" ||
      error.name === "AbortError"
    );
  }
  const err = error as { code?: string; message?: string; name?: string };
  return (
    err?.code === "ERR_NETWORK" ||
    err?.code === "ECONNABORTED" ||
    err?.code === "ERR_CANCELED" ||
    err?.message === "Network Error" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError"
  );
};

/**
 * Determines whether an error should be reported to Sentry.
 * It filters out:
 * - Network errors / connection drops / aborted requests (no HTTP response received)
 * - Client HTTP errors (status < 500, e.g. 4xx)
 * @param error - The error to evaluate.
 * @returns True if the error should be captured in Sentry (e.g. 5xx or unhandled runtime exceptions), false otherwise.
 */
export const shouldReportErrorToSentry = (error: unknown): boolean => {
  if (!error) return false;

  if (isAxiosError(error)) {
    // If there is no response (network error, offline, canceled), do not report
    if (!error.response) {
      return false;
    }
    // If it's a client error (< 500), do not report
    if (error.response.status && error.response.status < 500) {
      return false;
    }
    return true;
  }

  if (isNetworkError(error)) {
    return false;
  }

  const status =
    (error as { response?: { status?: number }; status?: number })?.response
      ?.status ?? (error as { status?: number })?.status;
  if (status && status < 500) {
    return false;
  }

  return true;
};

/**
 * Checks if the given errors object contains a specific error for a specific field.
 * @param errors - The errors object returned from the API. e.g., { name: ["validation.unique", "validation.format"] }
 * @param field - The field to check for errors. e.g., "name"
 * @param errorKey - The specific error key to check for. e.g., "validation.unique"
 * @returns True if the error exists, false otherwise.
 */
export const isAPIError = (
  data: {
    errors?: { [key: string]: string[] };
    message?: string;
  },
  field: string,
  errorKey: string,
): boolean => {
  if (!data || !field || !errorKey) return false;
  const errors = data.errors;
  if (errors) {
    if (!Array.isArray(errors[field])) return false;
    if (errors[field]?.includes(errorKey)) return true;
  } else if (data.message) {
    // Fallback for non-standard error responses
    // for specific duplicated name error messages
    const messageLower = data.message.toLowerCase();
    if (
      messageLower.includes("name") &&
      messageLower.includes("already exists") &&
      errorKey === "validation.unique" &&
      field === "name"
    ) {
      return true;
    }
  }
  return false;
};
